import React, { useEffect, useRef, useState } from 'react';

/**
 * Wiederverwendbares Unterschriftenfeld (Canvas + Pointer-Events).
 *
 * Herkunft: die Zeichenlogik stand ausschliesslich in
 * src/components/crm/SignaturePadPage.tsx (oeffentliche Angebots-Unterschrift
 * ohne Login). Mit der Einweisung in den SiGe-Plan braucht sie eine zweite
 * Stelle — und zwei Kopien einer Canvas-Behandlung mit Rotationssperre,
 * dvh-Rechnerei und Pointer-Koordinaten driften garantiert auseinander.
 *
 * ─── Warum die DataURL verkleinert wird ────────────────────────────────────
 * Ein 900x300-PNG aus dem Canvas wiegt roh gut 15 kB. Auf einem
 * Einweisungsblatt stehen dreissig Unterschriften: das sind knapp ein halbes
 * Megabyte, die als jsonb im Datensatz liegen, bei JEDEM Laden der Ablage
 * ueber die Leitung gehen und noch einmal im PDF landen. Die Rueckgabe wird
 * deshalb auf `maxBreite` heruntergerechnet. Wer die volle Aufloesung braucht
 * (die Angebots-Seite gibt sie unveraendert weiter), setzt `maxBreite` auf die
 * Canvas-Breite.
 *
 * ─── Warum Software-Querformat ─────────────────────────────────────────────
 * Im Hochformat ist das Feld zu schmal zum Unterschreiben. Dreht das Geraet
 * die Ansicht wegen aktiver Rotationssperre nicht selbst, dreht diese
 * Komponente die eigene Oberflaeche per CSS um 90°. Der Canvas bleibt dabei
 * gemountet — bereits gezogene Striche gehen nicht verloren.
 */

export interface SignaturePadProps {
  /** Ueberschrift ueber dem Feld. */
  titel: string;
  /** Erklaerender Satz im Hochformat/Querformat neben der Ueberschrift. */
  hinweis?: string;
  /** Aufloesung der Zeichenflaeche in Canvas-Koordinaten. */
  breite?: number;
  hoehe?: number;
  /**
   * Groesste Breite der zurueckgegebenen PNG-DataURL. Standard 480 px — auf
   * dem Blatt ist eine Unterschrift rund 50 mm breit, mehr Punkte sieht dort
   * niemand.
   */
  maxBreite?: number;
  /** Beschriftung des bestaetigenden Knopfs. */
  uebernehmenLabel?: string;
  /** Beschriftung waehrend `busy`. */
  busyLabel?: string;
  /** Laufender Vorgang: alle Knoepfe gesperrt. */
  busy?: boolean;
  /** Fehlermeldung unter dem Feld. `null` blendet den Kasten aus. */
  fehler?: string | null;
  /**
   * Ganzflaechig ueber der Anwendung liegen statt im Seitenfluss. Fuer die
   * Einweisung auf dem Handy: unterschrieben wird im Vollbild, sonst ist das
   * Feld zwischen Kopfdaten und Teilnehmerliste zu klein.
   */
  alsOverlay?: boolean;
  /** Bekommt die fertige PNG-DataURL. */
  onUebernehmen: (dataUrl: string) => void;
  /** Ohne Handler erscheint kein Abbrechen-Knopf (oeffentliche Seite hat keinen). */
  onAbbrechen?: () => void;
  abbrechenLabel?: string;
}

/**
 * Rechnet den Canvas-Inhalt auf hoechstens `maxBreite` herunter und gibt ihn
 * als PNG-DataURL zurueck.
 *
 * Kein Hochskalieren: ist der Canvas bereits schmaler, bleibt er, wie er ist.
 * Schlaegt das Verkleinern fehl (kein 2D-Kontext), wird die volle Aufloesung
 * zurueckgegeben — eine Unterschrift zu verlieren waere schlimmer als ein paar
 * Kilobyte zu viel.
 */
export const verkleinereSignatur = (canvas: HTMLCanvasElement, maxBreite: number): string => {
  try {
    if (!maxBreite || canvas.width <= maxBreite) return canvas.toDataURL('image/png');
    const faktor = maxBreite / canvas.width;
    const klein = document.createElement('canvas');
    klein.width = Math.round(canvas.width * faktor);
    klein.height = Math.max(1, Math.round(canvas.height * faktor));
    const ctx = klein.getContext('2d');
    if (!ctx) return canvas.toDataURL('image/png');
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(canvas, 0, 0, klein.width, klein.height);
    return klein.toDataURL('image/png');
  } catch {
    return canvas.toDataURL('image/png');
  }
};

export const SignaturePad: React.FC<SignaturePadProps> = ({
  titel,
  hinweis,
  breite = 900,
  hoehe = 300,
  maxBreite = 480,
  uebernehmenLabel = 'Übernehmen',
  busyLabel = 'Überträgt…',
  busy = false,
  fehler = null,
  alsOverlay = false,
  onUebernehmen,
  onAbbrechen,
  abbrechenLabel = 'Abbrechen',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  /**
   * Riegel gegen den zweiten Fingertipp auf „Übernehmen".
   *
   * `busy` steht erst nach dem naechsten Zeichnen; zwei schnelle Beruehrungen
   * — mit Handschuh der Normalfall — laufen davor beide durch. Auf der
   * oeffentlichen Seite verbraucht der zweite Anlauf einen bereits
   * entwerteten Einmal-Token, in der Einweisung setzt er dieselbe Unterschrift
   * ein zweites Mal. Der Riegel faellt beim Loeschen und bei einer
   * Fehlermeldung wieder — beides heisst: es geht von vorn los.
   */
  const uebergeben = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  // Querformat erzwingen (Hinweis-Overlay): im Hochformat ist das Feld zu klein.
  const [portrait, setPortrait] = useState<boolean>(() => window.matchMedia('(orientation: portrait)').matches);
  // Software-Querformat: dreht die UI per CSS um 90°, wenn das Geraet die
  // Browser-Ansicht nicht selbst dreht (Rotationssperre).
  const [rotated, setRotated] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(orientation: portrait)');
    const fn = (e: MediaQueryListEvent) => setPortrait(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e293b';
  }, []);

  // Pointer-Position in Canvas-Koordinaten (Canvas ist responsiv skaliert).
  // Im Software-Querformat (rotate 90°) ist die Bounding-Box achsenparallel:
  // Canvas-x laeuft am Bildschirm nach unten, Canvas-y nach links.
  const pos = (e: React.PointerEvent) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    if (rotated) {
      return {
        x: ((e.clientY - r.top) / r.height) * c.width,
        y: ((r.right - e.clientX) / r.width) * c.height,
      };
    }
    return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height };
  };

  const onDown = (e: React.PointerEvent) => {
    if (busy) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const onMove = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasStrokes(true);
  };

  const onUp = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    setHasStrokes(false);
    uebergeben.current = false;
  };

  // Nach einer Fehlermeldung darf derselbe Strich erneut abgeschickt werden.
  useEffect(() => {
    if (fehler) uebergeben.current = false;
  }, [fehler]);

  const uebernehmen = () => {
    const c = canvasRef.current;
    if (!c || !hasStrokes || busy || uebergeben.current) return;
    uebergeben.current = true;
    onUebernehmen(verkleinereSignatur(c, maxBreite));
  };

  return (
    <div
      className={rotated
        ? 'fixed z-40 bg-slate-100 p-2 flex flex-col'
        : `${alsOverlay ? 'fixed inset-0 z-40 ' : 'min-h-screen '}bg-slate-100 flex flex-col items-center justify-center p-4 gap-4`}
      // dvh/dvw statt vh/vw: iOS Safari zaehlt URL- und Bottom-Leiste in
      // 100vh MIT — die zentrierte Karte ragte hinter die Leiste, die
      // Buttons unter dem Unterschriftenfeld waren auf dem iPhone
      // unerreichbar. dvh ist der tatsaechlich sichtbare Viewport; dazu das
      // Safe-Area-Polster fuer den Home-Indicator.
      // `touchAction: 'none'` NUR im Overlay: dort liegt die Anwendung darunter
      // und wuerde beim Zeichnen mitscrollen, sobald der Finger neben den
      // Canvas geraet (der hat seine eigene Sperre). Die oeffentliche Seite
      // rendert ohne Overlay im Seitenfluss und bleibt unveraendert scrollbar.
      style={rotated
        ? {
          top: 0,
          left: 0,
          width: '100dvh',
          height: '100dvw',
          transform: 'rotate(90deg) translateY(-100%)',
          transformOrigin: 'top left',
          ...(alsOverlay ? { touchAction: 'none' as const, overscrollBehavior: 'contain' as const } : {}),
        }
        : {
          minHeight: '100dvh',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
          ...(alsOverlay ? { touchAction: 'none', overscrollBehavior: 'contain' } : {}),
        }}
    >
      {/* Hochformat: Overlay mit Dreh-Hinweis + Software-Querformat-Button.
          Canvas bleibt gemountet (Striche gehen nicht verloren). */}
      {portrait && !rotated && (
        <div className="fixed inset-0 z-[100] bg-slate-900/90 flex flex-col items-center justify-center gap-4 p-8 text-center">
          <svg className="w-16 h-16 text-white animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="7" y="3" width="10" height="18" rx="2" transform="rotate(90 12 12)" />
            <path d="M4 8V6a2 2 0 0 1 2-2h2" />
          </svg>
          <div className="text-white text-base font-bold">Bitte Smartphone quer halten</div>
          <div className="text-slate-300 text-sm">Dreht dein Gerät die Ansicht nicht (Rotationssperre), nutze den Button:</div>
          <button onClick={() => setRotated(true)} className="px-5 py-3 min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl transition-all">
            Im Querformat unterschreiben
          </button>
          {onAbbrechen && (
            <button onClick={onAbbrechen} className="px-5 py-3 min-h-[44px] text-slate-300 hover:text-white text-sm font-medium transition-all">
              {abbrechenLabel}
            </button>
          )}
        </div>
      )}
      <div className={rotated
        ? 'bg-white rounded-2xl border border-slate-200 p-3 flex flex-col gap-2 h-full w-full'
        : 'bg-white rounded-2xl shadow-sm border border-slate-200 p-5 w-full max-w-2xl space-y-4'}
      >
        <div className={rotated ? 'flex items-center justify-between gap-2' : ''}>
          <h1 className="text-base font-bold text-slate-800">{titel}</h1>
          {rotated ? (
            <button onClick={() => setRotated(false)} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 rounded-lg transition-all">
              Drehung beenden
            </button>
          ) : (
            hinweis ? <p className="text-xs text-slate-500">{hinweis}</p> : null
          )}
        </div>

        <canvas
          ref={canvasRef}
          width={breite}
          height={hoehe}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          className={`w-full bg-white border-2 border-dashed border-slate-300 rounded-xl ${rotated ? 'flex-1 min-h-0' : ''}`}
          style={{ touchAction: 'none' }}
        />
        {fehler && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">{fehler}</div>
        )}
        <div className="flex gap-2 justify-end">
          {onAbbrechen && (
            <button onClick={onAbbrechen} disabled={busy} className="px-4 py-2.5 min-h-[44px] text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-xl transition-all disabled:opacity-50">
              {abbrechenLabel}
            </button>
          )}
          <button onClick={clear} disabled={busy} className="px-4 py-2.5 min-h-[44px] text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-xl transition-all disabled:opacity-50">
            Löschen
          </button>
          <button onClick={uebernehmen} disabled={!hasStrokes || busy} className="px-5 py-2.5 min-h-[44px] text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all disabled:opacity-50">
            {busy ? busyLabel : uebernehmenLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
